import { useEffect, useRef } from "react";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import i18n from "i18next";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { CatalogPart } from "src/api/generated/erp/db/types/tables/catalogPart.ts";
import { AavoFormContentParams } from "src/components/common/forms/AavoForm.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { CatalogPartEditApi_FormInitData } from "src/api/generated/erp/parts/catalogPart/api/catalogPartEditApi.ts";
import { logError } from "src/errorHandling/errorLogging.ts";

export interface CatalogPartFormContentProps extends AavoFormContentParams<CatalogPartFormValues> {
	isNewRecord: boolean;
	initData: Omit<CatalogPartEditApi_FormInitData, "catalogPart">;
}

export interface CatalogPartFormValues
	extends Omit<CatalogPart, "catalogPartId" | "lastUpdated" | "partNo" | "active" | "createdAt" | "createdByUserId"> {
	numberSequenceId: number | null;
	manuallyInsertedPartNumber: string;
	partNumberInsertType: PartNumberInsertType;
}

export type PartNumberInsertType = "numberSequenceId" | "manuallyInsertedPartNumber";

export const CatalogPartFormContent = ({
	control,
	watch,
	setValue,
	clearErrors,
	formState,
	trigger,
	isNewRecord,
	initData: { numberSequenceOptions, erpUnitOptions, partCategoryOptions, configurationProductFamilyOptions },
}: CatalogPartFormContentProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const configuratorEnabled = tenantConfig.erp.configuratorEnabled;

	const isConfigurable = watch("isConfigurable");
	const insertType = watch("partNumberInsertType");
	const insertTypeValidationInitializedRef = useRef(false);
	const configurableValidationInitializedRef = useRef(false);

	useEffect(() => {
		if (!insertTypeValidationInitializedRef.current) {
			insertTypeValidationInitializedRef.current = true;
			return;
		}
		if (isNewRecord && insertType != null) {
			if (insertType === "numberSequenceId") {
				clearErrors("manuallyInsertedPartNumber");
			} else {
				clearErrors("numberSequenceId");
			}
			trigger(["partNumberInsertType", "numberSequenceId", "manuallyInsertedPartNumber"]).catch(logError);
		}
	}, [clearErrors, insertType, isNewRecord, trigger]);

	useEffect(() => {
		if (!configurableValidationInitializedRef.current) {
			configurableValidationInitializedRef.current = true;
			return;
		}
		if (!isConfigurable) {
			clearErrors("configurationProductFamilyId");
		}
		trigger(["isConfigurable", "configurationProductFamilyId"]).catch(logError);
	}, [clearErrors, isConfigurable, trigger]);

	return (
		<>
			{isNewRecord && (
				<FormSelectField
					control={control}
					name={"partNumberInsertType"}
					label={i18n.t("part_number_insert_method")}
					options={["numberSequenceId", "manuallyInsertedPartNumber"]}
					getOptionKey={(o: PartNumberInsertType) => o}
					getOptionLabel={(o) => {
						switch (o) {
							case "numberSequenceId":
								return i18n.t("number_sequence");
							case "manuallyInsertedPartNumber":
								return i18n.t("part_number");
						}
					}}
					rules={requireRule()}
					startNewGridRow
					onChange={(newInsertType) => {
						if (newInsertType === "numberSequenceId") {
							setValue("manuallyInsertedPartNumber", "");
							clearErrors("manuallyInsertedPartNumber");
						} else {
							setValue("numberSequenceId", null);
							clearErrors("numberSequenceId");
						}
					}}
				/>
			)}
			{isNewRecord && insertType === "numberSequenceId" && (
				<FormSelectField
					control={control}
					name={"numberSequenceId"}
					label={i18n.t("number_sequence")}
					options={numberSequenceOptions}
					getOptionKey={(o) => o.numberSequenceId}
					getOptionLabel={(o) => o.sequenceName}
					rules={requireRule()}
				/>
			)}
			{insertType === "manuallyInsertedPartNumber" && (
				<FormTextField
					control={control}
					name={"manuallyInsertedPartNumber"}
					label={i18n.t("part_number")}
					rules={{
						...requireRule(),
						validate: (value) => (value ?? "").trim().length > 0 || i18n.t("required"),
					}}
				/>
			)}
			<FormTextField
				control={control}
				name={"description_1"}
				autoFocus={isNewRecord}
				label={i18n.t("description")}
				rules={{
					...requireRule(),
					maxLength: {
						value: 255,
						message: i18n.t("text_too_long"),
					},
				}}
				startNewGridRow
			/>
			<FormTextField
				control={control}
				name={"description_2"}
				label={i18n.t("description_2")}
				rules={{
					maxLength: {
						value: 255,
						message: i18n.t("text_too_long"),
					},
				}}
			/>
			<FormTextField control={control} name={"manufacturerPartNumber"} label={i18n.t("manufacturer_code")} />
			<FormTextField control={control} name={"additionalNumber"} label={i18n.t("additional_code")} />
			<FormSelectField
				control={control}
				name={"categoryId"}
				label={i18n.t("category")}
				options={partCategoryOptions}
				getOptionKey={(o) => o.partCategoryId}
				getOptionLabel={(o) => o.categoryName}
				rules={requireRule()}
			/>
			<FormAsyncUserSelectField
				control={control}
				name={"responsiblePersonId"}
				label={i18n.t("responsible_person")}
				rules={requireRule()}
			/>
			<FormSelectField
				control={control}
				name={"partUnitId"}
				label={i18n.t("unit")}
				options={erpUnitOptions}
				getOptionKey={(o) => o.unitId}
				getOptionLabel={(o) => o.unitName}
				rules={requireRule()}
				disabled={!isNewRecord}
				onChange={async (newPartUnitId) => {
					if (!formState.dirtyFields.designUnitId) {
						setValue("designUnitId", newPartUnitId);
						await trigger()
					}
				}}
			/>
			<FormSelectField
				control={control}
				name={"designUnitId"}
				label={i18n.t("design_unit")}
				options={erpUnitOptions}
				getOptionKey={(o) => o.unitId}
				getOptionLabel={(o) => o.unitName}
				rules={requireRule()}
			/>
			<FormNumberField
				control={control}
				name={"designUnitFactor"}
				label={i18n.t("design_factor")}
				rules={requireRule()}
			/>
			<FormNumberField control={control} name={"weight"} label={i18n.t("weight")} rules={requireRule()} />
			{configuratorEnabled && (
				<>
					<FormCheckbox
						control={control}
						name={"isConfigurable"}
						label={i18n.t("configurable")}
						onChange={(newIsConfigurable) => {
							if (!newIsConfigurable) {
								setValue("configurationProductFamilyId", null);
								clearErrors("configurationProductFamilyId");
							}
						}}
					/>
					{isConfigurable && (
						<FormSelectField
							control={control}
							name={"configurationProductFamilyId"}
							label={i18n.t("product_family")}
							options={configurationProductFamilyOptions}
							getOptionKey={(o) => o.configurationProductFamilyId}
							getOptionLabel={(o) => o.name}
							disabled={!(isNewRecord || formState.dirtyFields.isConfigurable)}
							rules={requireRule()}
						/>
					)}
				</>
			)}
		</>
	);
};
